import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { authFetch } from '../common/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STUN_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
];

const MAX_SIGNAL_RETRIES = 3;
const MAX_POLL_FAILURES = 5;

const PeerManager = forwardRef(({ targetUserId, onStatusMessage, onSnapshot, onConnectionChange }, ref) => {
    const pcRef = useRef(null);
    const pollingRef = useRef(null);
    const heartbeatRef = useRef(null);
    const closedRef = useRef(false);
    const retryTimerRef = useRef(null);
    const iceCandidateQueue = useRef([]);
    const reconnectingRef = useRef(false);
    const pollFailCountRef = useRef(0);

    useImperativeHandle(ref, () => ({
        close: () => {
            closedRef.current = true;
            cleanup();
        }
    }));

    const cleanup = () => {
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
        if (pcRef.current) {
            pcRef.current.close();
            pcRef.current = null;
        }
        iceCandidateQueue.current = [];
        reconnectingRef.current = false;
    };

    const sendSignal = async (type, data) => {
        for (let attempt = 0; attempt < MAX_SIGNAL_RETRIES; attempt++) {
            try {
                await authFetch(`${BACKEND_URL}/api/v1/studyrooms/${targetUserId}/signal`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ type, data })
                });
                return true;
            } catch (error) {
                console.error(`Failed to send signal (attempt ${attempt + 1}):`, error);
                if (attempt < MAX_SIGNAL_RETRIES - 1) {
                    await new Promise(r => setTimeout(r, 1000));
                }
            }
        }
        return false;
    };

    const createPeerConnection = () => {
        const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS });
        pcRef.current = pc;

        pc.onicecandidate = (event) => {
            if (event.candidate && !closedRef.current) {
                sendSignal('ice', JSON.stringify(event.candidate));
            }
        };

        pc.oniceconnectionstatechange = () => {
            const state = pc.iceConnectionState;
            if (closedRef.current) return;

            if (state === 'connected' || state === 'completed') {
                reconnectingRef.current = false;
                pollFailCountRef.current = 0;
                onConnectionChange('connected');
                // 관찰자 등록 유지를 위한 heartbeat 시작 (30초 간격)
                if (!heartbeatRef.current) {
                    heartbeatRef.current = setInterval(() => {
                        if (closedRef.current) return;
                        sendSignal('heartbeat', '').catch(() => {});
                    }, 30000);
                }
            } else if (state === 'disconnected' || state === 'failed') {
                if (reconnectingRef.current) return; // 중복 재연결 방지
                reconnectingRef.current = true;
                onConnectionChange(state);
                cleanup();
                retryTimerRef.current = setTimeout(() => {
                    if (!closedRef.current) {
                        reconnectingRef.current = false;
                        initiateConnection();
                    }
                }, 3000);
            }
        };

        pc.ondatachannel = (event) => {
            const channel = event.channel;
            setupDataChannel(channel);
        };

        return pc;
    };

    const setupDataChannel = (channel) => {
        channel.binaryType = 'arraybuffer';
        channel.onmessage = (event) => {
            if (typeof event.data === 'string') {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'status') {
                        onStatusMessage(msg);
                    }
                } catch (e) {
                    // ignore parse errors
                }
            } else {
                const blob = new Blob([event.data], { type: 'image/jpeg' });
                onSnapshot(blob);
            }
        };
    };

    const initiateConnection = async () => {
        try {
            const pc = createPeerConnection();

            // DataChannel 생성 + offer
            const statusChannel = pc.createDataChannel('status');
            const snapshotChannel = pc.createDataChannel('snapshot');
            setupDataChannel(statusChannel);
            setupDataChannel(snapshotChannel);

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            const sent = await sendSignal('offer', JSON.stringify(offer));

            if (!sent) {
                onConnectionChange('failed');
                // offer 전송 실패 시 재시도
                cleanup();
                retryTimerRef.current = setTimeout(() => {
                    if (!closedRef.current) initiateConnection();
                }, 5000);
                return;
            }

            // 폴링 시작: answer/ICE 수신
            pollFailCountRef.current = 0;
            startPolling();
        } catch (error) {
            console.error('Failed to initiate connection:', error);
            onConnectionChange('failed');
        }
    };

    const startPolling = () => {
        if (pollingRef.current) return;

        pollingRef.current = setInterval(async () => {
            if (closedRef.current) {
                cleanup();
                return;
            }

            try {
                const response = await authFetch(`${BACKEND_URL}/api/v1/studyrooms/signal/pending`);
                const data = await response.json();
                pollFailCountRef.current = 0;

                if (data.success && data.messages.length > 0) {
                    for (const msg of data.messages) {
                        await handleSignalingMessage(msg);
                    }
                }
            } catch (error) {
                pollFailCountRef.current++;
                console.error(`Polling error (${pollFailCountRef.current}/${MAX_POLL_FAILURES}):`, error);
                if (pollFailCountRef.current >= MAX_POLL_FAILURES) {
                    onConnectionChange('failed');
                    cleanup();
                    retryTimerRef.current = setTimeout(() => {
                        if (!closedRef.current) initiateConnection();
                    }, 5000);
                }
            }
        }, 2000);
    };

    const flushIceCandidateQueue = async (pc) => {
        const candidates = [...iceCandidateQueue.current];
        iceCandidateQueue.current = [];
        for (const candidate of candidates) {
            try {
                await pc.addIceCandidate(candidate);
            } catch (e) {
                console.warn('Failed to add queued ICE candidate:', e);
            }
        }
    };

    const handleSignalingMessage = async (msg) => {
        const pc = pcRef.current;
        if (!pc || closedRef.current) return;

        try {
            if (msg.type === 'answer') {
                await pc.setRemoteDescription(JSON.parse(msg.data));
                await flushIceCandidateQueue(pc);
            } else if (msg.type === 'ice') {
                if (!pc.remoteDescription) {
                    iceCandidateQueue.current.push(JSON.parse(msg.data));
                } else {
                    await pc.addIceCandidate(JSON.parse(msg.data));
                }
            }
            // PeerManager는 관찰자 측이므로 offer를 받을 일이 없음
        } catch (error) {
            console.error('Failed to handle signaling message:', error);
        }
    };

    useEffect(() => {
        closedRef.current = false;
        initiateConnection();
        return () => {
            closedRef.current = true;
            // 퇴장 시 관찰자 해제 알림
            sendSignal('leave', '').catch(() => {});
            cleanup();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetUserId]);

    // 렌더링 없음 (로직 전용 컴포넌트)
    return null;
});

PeerManager.displayName = 'PeerManager';

export default PeerManager;
